import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Megaphone, Send } from 'lucide-react';
import { RecordAttachments } from './RecordAttachments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AnnouncementCard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);
  const [attachToEmail, setAttachToEmail] = useState(true);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [attachments, setAttachments] = useState([]);
  // Draft id used to store attachments before the announcement record exists
  const draftIdRef = useRef(`draft-${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`);
  const [draftId, setDraftId] = useState(draftIdRef.current);

  const load = () => {
    const url = isAdmin ? `${API}/announcements` : `${API}/announcements/public`;
    axios.get(url).then((res) => setItems(res.data || [])).catch(() => {});
  };

  useEffect(() => { load(); }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (e) => {
    e.preventDefault();
    if (sendEmail && !window.confirm('Send this announcement to the WHOLE team by email?')) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/announcements`, {
        subject,
        message,
        is_public: isPublic,
        send_email: sendEmail,
        attachment_ids: attachments.map((a) => a.id),
        attach_to_email: attachToEmail,
      });
      toast.success(sendEmail ? `Announcement emailed to ${res.data.sent_count} team member(s)` : 'Announcement published');
      setOpen(false);
      setSubject('');
      setMessage('');
      setAttachments([]);
      // New draft id for the next announcement
      const next = `draft-${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`;
      draftIdRef.current = next;
      setDraftId(next);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setSending(false);
    }
  };

  // Non-admins with no public announcements: hide the card entirely
  if (!isAdmin && items.length === 0) return null;

  return (
    <Card data-testid="announcement-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Megaphone className="h-4 w-4" /> Team Announcements
          </CardTitle>
          <CardDescription>
            {isAdmin ? 'Publish announcements to the team dashboard and/or email everyone' : 'Latest announcements from management'}
          </CardDescription>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setOpen(true)} data-testid="send-announcement-btn">
            <Send className="h-4 w-4 mr-1" /> New Announcement
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No announcements yet.</p>
        ) : (
          <div className="divide-y">
            {items.slice(0, isAdmin ? 3 : 5).map((a) => (
              <div key={a.id} className="py-2" data-testid={`announcement-item-${a.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <button type="button" className="min-w-0 text-left flex-1" onClick={() => setExpanded(expanded === a.id ? null : a.id)}>
                    <p className="text-sm font-medium truncate">{a.subject}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      by {a.sent_by} · {a.created_at?.split('T')[0]}
                    </p>
                  </button>
                  {isAdmin && (
                    <div className="flex gap-1 shrink-0">
                      {a.is_public !== false && <Badge variant="outline" className="text-[10px]">Public</Badge>}
                      {a.email_sent !== false && <Badge variant="secondary" className="text-[10px]">{a.sent_count}/{a.recipients_count} emailed</Badge>}
                    </div>
                  )}
                </div>
                {expanded === a.id && (
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{a.message}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="h-5 w-5" /> New Team Announcement
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={send} className="space-y-3">
              <div className="space-y-1">
                <Label>Subject *</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} required placeholder="e.g. Friday team meeting at 14:00" data-testid="announcement-subject" />
              </div>
              <div className="space-y-1">
                <Label>Message *</Label>
                <Textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} required placeholder="Write your announcement or reminder..." data-testid="announcement-message" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={isPublic} onCheckedChange={(c) => setIsPublic(!!c)} data-testid="announcement-public-check" />
                  Public — show on every team member&apos;s dashboard
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={sendEmail} onCheckedChange={(c) => setSendEmail(!!c)} data-testid="announcement-email-check" />
                  Also email the whole team (from crm@ikworx.co.za)
                </label>
                {sendEmail && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer pl-6">
                    <Checkbox checked={attachToEmail} onCheckedChange={(c) => setAttachToEmail(!!c)} data-testid="announcement-attach-email-check" />
                    Include attached files in the email
                  </label>
                )}
              </div>

              <div className="border-t pt-3">
                <RecordAttachments
                  parentType="announcement"
                  parentId={draftId}
                  canModify={true}
                  onChange={setAttachments}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={sending} data-testid="announcement-submit">
                  {sending ? 'Sending...' : 'Publish Announcement'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};
